/**
 * Buildings — landlord list of every property/building with at-a-glance stats.
 * Route: /buildings (subscriber-only).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ArrowRight, TrendingUp, Wrench, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, Card, Button } from "@/components/ui/Primitives";
import { usePageTitle } from "@/lib/seo";
import { toast } from "sonner";

export default function Buildings() {
  usePageTitle("Building Hub — Central Rentals Canada");
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/landlord/buildings");
        setBuildings(data?.buildings || []);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 402) {
          setError("Building Hub is a subscriber-only feature. Pick a plan on the Billing page to unlock it.");
        } else {
          setError("Couldn't load your buildings. Please refresh.");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-5 py-8" data-testid="buildings-page">
      <PageHeader
        title="Building Hub"
        subtitle="Everything about each property — units, tenants, financials, and quick actions — in one command centre."
        icon={Building2}
      />

      {loading && (
        <div className="mt-10 flex items-center justify-center text-slate-500" data-testid="buildings-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your buildings…
        </div>
      )}

      {!loading && error && (
        <Card className="mt-8 p-6 bg-amber-50 border-amber-200 text-amber-900" data-testid="buildings-error">
          <div className="text-sm font-semibold">{error}</div>
          <Link to="/billing" className="mt-3 inline-block text-sm underline underline-offset-4">Open Billing →</Link>
        </Card>
      )}

      {!loading && !error && buildings.length === 0 && (
        <Card className="mt-8 p-8 text-center" data-testid="buildings-empty">
          <Building2 className="w-8 h-8 mx-auto text-slate-300" />
          <div className="mt-3 font-display text-lg font-bold text-slate-800">You haven&apos;t added a building yet</div>
          <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">Add your first property to unlock the command centre — units, leases, maintenance, and financials in one place.</p>
          <Link to="/properties" className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm">
            Add a property <ArrowRight className="w-4 h-4" />
          </Link>
        </Card>
      )}

      {!loading && !error && buildings.length > 0 && (
        <div className="mt-8 grid md:grid-cols-2 gap-5" data-testid="buildings-grid">
          {buildings.map((b) => (
            <Link
              key={b.property_id}
              to={`/buildings/${b.property_id}`}
              data-testid={`building-card-${b.property_id}`}
              className="block bg-white rounded-2xl border border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all overflow-hidden"
            >
              {b.image_url ? (
                <img src={b.image_url} alt={b.name} loading="lazy" className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                  <Building2 className="w-10 h-10 text-slate-400" />
                </div>
              )}
              <div className="p-5">
                <div className="font-display text-lg font-bold text-slate-900">{b.name || "Unnamed building"}</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{b.address}</div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
                  <Stat label="Occupancy" value={`${b.occupancy_pct}%`} tone={b.occupancy_pct >= 80 ? "emerald" : "amber"} />
                  <Stat label="Monthly rent" value={`$${(b.monthly_rent_cad || 0).toLocaleString()}`} />
                  <Stat label="Open jobs" value={b.open_maintenance} tone={b.open_maintenance > 0 ? "rose" : "slate"} icon={Wrench} />
                </div>
                <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                  <span>{b.units_occupied}/{b.units_total} units occupied</span>
                  <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
                    Open hub <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "slate", icon: Icon }) {
  const toneCls = {
    emerald: "bg-emerald-50 text-emerald-800",
    amber: "bg-amber-50 text-amber-800",
    rose: "bg-rose-50 text-rose-800",
    slate: "bg-slate-50 text-slate-800",
  }[tone] || "bg-slate-50 text-slate-800";
  return (
    <div className={`rounded-lg px-2.5 py-2 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="mt-0.5 text-sm font-bold tabular-nums">{value}</div>
    </div>
  );
}
